import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * 商品价格。变体切换后更新展示。
 * 勾选推荐时，只有吸底栏把到手价 / Save 相加，商详和折扣码条保持单品。
 */
class ProductPrice extends HTMLElement {
  /** @type {((event: Event) => void) | null} */
  #onRelatedChange = null;

  /** @type {Element | null} */
  #listenerRoot = null;

  /** @type {string} */
  #baseHTML = '';

  /** @type {string} */
  #stickyHTML = '';

  /** @type {string} */
  #stickySaveHTML = '';

  /** @type {string} */
  #codeSaveHTML = '';

  connectedCallback() {
    this.#listenerRoot = this.closest('.shopify-section, dialog');
    if (!this.#listenerRoot) return;
    this.#listenerRoot.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    this.#onRelatedChange = (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('.ug-related-product-checkbox')) return;
      this.#applyRelatedTotal();
    };
    this.#listenerRoot.addEventListener('change', this.#onRelatedChange);
    this.#snapshot();
    this.#applyRelatedTotal();
  }

  disconnectedCallback() {
    const root = this.#listenerRoot;
    this.#listenerRoot = null;
    if (!root) return;
    root.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    if (this.#onRelatedChange) root.removeEventListener('change', this.#onRelatedChange);
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    const nextHost = event.detail.data.html.querySelector(
      `product-price[data-product-id="${this.dataset.productId}"]`
    );

    if (nextHost instanceof HTMLElement) {
      this.dataset.basePriceCents = nextHost.dataset.basePriceCents || this.dataset.basePriceCents;
      this.dataset.baseCompareCents = nextHost.dataset.baseCompareCents || this.dataset.baseCompareCents;
      if (nextHost.dataset.moneyFormat) this.dataset.moneyFormat = nextHost.dataset.moneyFormat;
      if (nextHost.dataset.saveLabel) this.dataset.saveLabel = nextHost.dataset.saveLabel;
    }

    if (newPrice && currentPrice && currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice);
    }
    // 吸底栏可能还留着上一档叠价，先拉回单品再拍快照，避免把合计当成底稿
    this.#restoreSticky();
    this.#snapshot();
    this.#applyRelatedTotal();
    // 折扣码条也会在同一次 variant:update 里换 HTML，等它写完再记下单品 Save
    queueMicrotask(() => {
      const codeSave = this.#sectionRoot()?.querySelector('.discount-code-save');
      this.#codeSaveHTML = codeSave instanceof HTMLElement ? codeSave.outerHTML : '';
      this.#applyRelatedTotal();
    });
  };

  /** 当前详情里勾选推荐的到手价 / 划线价（分） */
  #relatedExtras() {
    const info = this.closest('.product-information');
    const section = this.closest('.shopify-section');
    const root = info?.querySelector('.ug-related-product-checkbox') ? info : section;
    if (!root) return { price: 0, compare: 0 };
    return Array.from(root.querySelectorAll('.ug-related-product-checkbox:checked')).reduce(
      (sum, input) => {
        if (!(input instanceof HTMLInputElement) || input.disabled) return sum;
        const price = Number(input.dataset.priceCents) || 0;
        const compare = Number(input.dataset.compareCents) || price;
        return { price: sum.price + price, compare: sum.compare + compare };
      },
      { price: 0, compare: 0 }
    );
  }

  #snapshot() {
    const price = this.querySelector('[ref="priceContainer"]');
    this.#baseHTML = price instanceof HTMLElement ? price.innerHTML : '';
    const sticky = this.#stickyPriceEl();
    this.#stickyHTML = sticky instanceof HTMLElement ? sticky.innerHTML : '';
    const stickySave = this.#sectionRoot()?.querySelector('.sticky-bottom-bar__discount');
    this.#stickySaveHTML = stickySave instanceof HTMLElement ? stickySave.outerHTML : '';
    const codeSave = this.#sectionRoot()?.querySelector('.discount-code-save');
    this.#codeSaveHTML = codeSave instanceof HTMLElement ? codeSave.outerHTML : '';
  }

  /** 吸底栏价格和 Save 回到单品快照 */
  #restoreSticky() {
    const sticky = this.#stickyPriceEl();
    if (sticky instanceof HTMLElement && this.#stickyHTML) sticky.innerHTML = this.#stickyHTML;
    this.#restoreOuter('.sticky-bottom-bar__discount', this.#stickySaveHTML);
  }

  /** @returns {Element | null} */
  #sectionRoot() {
    if (this.closest('#quick-add-dialog, quick-add-dialog, .quick-add-modal')) return null;
    return this.closest('.shopify-section');
  }

  /**
   * @param {string} selector
   * @param {string} html
   */
  #restoreOuter(selector, html) {
    const root = this.#sectionRoot();
    const current = root?.querySelector(selector);
    if (html) {
      if (current) current.outerHTML = html;
      return;
    }
    if (current) current.remove();
  }

  /** @returns {HTMLElement | null} */
  #stickyPriceEl() {
    if (this.closest('#quick-add-dialog, quick-add-dialog, .quick-add-modal')) return null;
    const section = this.closest('.shopify-section');
    const sticky = section?.querySelector('.sticky-bottom-bar__price');
    return sticky instanceof HTMLElement ? sticky : null;
  }

  #applyRelatedTotal() {
    // 商详到手价 / Save、折扣码条始终回单品
    this.#restorePdp();
    const extras = this.#relatedExtras();
    if (extras.price <= 0) {
      this.#restoreSticky();
      return;
    }

    const basePrice = Number(this.dataset.basePriceCents) || 0;
    const baseCompare = Number(this.dataset.baseCompareCents) || basePrice;
    // 只有吸底栏叠多选；数量不乘金额
    const totalPrice = basePrice + extras.price;
    const totalCompare = baseCompare + extras.compare;
    this.#syncSticky(totalPrice, totalCompare);
    // % 和固定金额不能相加，叠加时统一换成「划线 − 到手」的金额
    this.#writeStickySave(Math.max(0, totalCompare - totalPrice));
  }

  /** 商详价格和折扣码条 Save 回到单品快照 */
  #restorePdp() {
    const price = this.querySelector('[ref="priceContainer"]');
    if (price instanceof HTMLElement && this.#baseHTML) price.innerHTML = this.#baseHTML;
    this.#restoreOuter('.discount-code-save', this.#codeSaveHTML);
  }

  /**
   * 吸底栏展示主品 + 勾选推荐的合计到手价
   * @param {number} price
   * @param {number} compare
   */
  #syncSticky(price, compare) {
    if (this.closest('#quick-add-dialog, quick-add-dialog, .quick-add-modal')) return;
    const section = this.closest('.shopify-section');
    const sticky = section?.querySelector('.sticky-bottom-bar__price');
    if (!(sticky instanceof HTMLElement)) return;
    const formattedPrice = this.#formatCents(price);
    const formattedCompare = this.#formatCents(compare);
    const ins = sticky.querySelector('ins');
    const del = sticky.querySelector('del');
    if (ins instanceof HTMLElement) {
      ins.textContent = formattedPrice;
      if (del instanceof HTMLElement) {
        del.textContent = formattedCompare;
        del.hidden = compare <= price;
      }
      return;
    }
    sticky.textContent = formattedPrice;
  }

  /**
   * 勾选推荐后，只改吸底栏 Save
   * @param {number} saveCents
   */
  #writeStickySave(saveCents) {
    const root = this.#sectionRoot();
    if (!root) return;
    const label = (this.dataset.saveLabel || 'Save').trim();
    const text = `${label} ${this.#formatSaveCents(saveCents)}`;
    this.#setSaveText(
      root.querySelector('.sticky-bottom-bar__discount'),
      text,
      saveCents,
      root.querySelector('.sticky-bottom-bar__price-container'),
      'sticky-bottom-bar__discount'
    );
  }

  /**
   * @param {Element | null} el
   * @param {string} text
   * @param {number} saveCents
   * @param {Element | null} parent
   * @param {string} className
   */
  #setSaveText(el, text, saveCents, parent, className) {
    if (saveCents <= 0) {
      if (el instanceof HTMLElement) el.hidden = true;
      return;
    }
    if (el instanceof HTMLElement) {
      el.hidden = false;
      el.textContent = text;
      return;
    }
    if (!(parent instanceof HTMLElement) || !className) return;
    const next = document.createElement('span');
    next.className = className;
    next.textContent = text;
    const shipping = parent.querySelector('.sticky-bottom-bar__shipping');
    if (shipping) parent.insertBefore(next, shipping);
    else parent.appendChild(next);
  }

  /**
   * Save 金额去掉末尾 .00，和 money_without_trailing_zeros 对齐
   * @param {number} cents
   */
  #formatSaveCents(cents) {
    return this.#formatCents(cents).replace(/([.,]00)(?=\s|$)/, '');
  }

  /**
   * 用店铺 money_format 把分转成展示文案
   * @param {number} cents
   */
  #formatCents(cents) {
    const template = this.dataset.moneyFormat || '${{amount}}';
    const currency = (this.dataset.currency || '').toUpperCase();
    const value = Math.max(0, Number(cents) || 0);

    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
      let thousands = ',';
      let decimal = '.';
      let precision = 2;
      if (key === 'currency') return currency;
      if (key === 'amount_no_decimals' || key === 'amount_no_decimals_with_comma_separator' || key === 'amount_no_decimals_with_space_separator') {
        precision = 0;
      }
      if (key === 'amount_with_comma_separator' || key === 'amount_no_decimals_with_comma_separator') {
        thousands = '.';
        decimal = ',';
      }
      if (key === 'amount_with_space_separator' || key === 'amount_no_decimals_with_space_separator' || key === 'amount_with_apostrophe_separator') {
        thousands = key === 'amount_with_apostrophe_separator' ? '\u2019' : ' ';
      }
      if (['JPY', 'KRW', 'VND'].includes(currency) && key === 'amount') precision = 0;

      const amount = value / 100;
      const fixed = amount.toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return precision > 0 ? parts.join(decimal) : parts[0];
    });
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
